import React, { useMemo } from "react";
import BulkUpdateFieldsModal from "./entity/BulkUpdateFieldsModal";
import { RENTAL_LEASE_STATUS_OPTIONS } from "../shared/utils/rentalLeaseTable";
import { getAssetLabel } from "../shared/utils/assetLabel";

/**
 * Bulk update modal for Rental Leases (platform BulkUpdateFieldsModal pattern).
 */
export default function BulkUpdateRentalLeasesModal({
  open,
  onClose,
  selectedCount = 0,
  assets = [],
  loading = false,
  onApply,
}) {
  const assetOptions = useMemo(() => {
    const list = Array.isArray(assets) ? assets : [];
    return [...list]
      .filter((a) => a && a._id)
      .map((a) => ({ id: String(a._id), name: getAssetLabel(a) }))
      .sort((x, y) => String(x.name).localeCompare(String(y.name)));
  }, [assets]);

  return (
    <BulkUpdateFieldsModal
      open={open}
      onClose={loading ? undefined : onClose}
      title={`Bulk update (${selectedCount || 0})`}
      subtitle="Choose values to apply to all selected leases."
      loading={loading}
      selectedCount={selectedCount}
      applyLabel="Apply update"
      onApply={(payload) => {
        if (!onApply) return;
        const next = {};
        if (Object.prototype.hasOwnProperty.call(payload, "status")) {
          next.status = String(payload.status || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "property_address")) {
          next.property_address = String(payload.property_address || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "landlords")) {
          next.landlords = String(payload.landlords || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "asset_id")) {
          next.asset_id = String(payload.asset_id || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "start_date")) {
          next.start_date = String(payload.start_date || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "end_date")) {
          next.end_date = String(payload.end_date || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "rent_amount")) {
          next.rent_amount = String(payload.rent_amount ?? "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "security_deposit")) {
          next.security_deposit = String(payload.security_deposit ?? "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "pet_deposit")) {
          next.pet_deposit = String(payload.pet_deposit ?? "").trim();
        }
        onApply(next);
      }}
      fields={[
        {
          id: "status",
          label: "Status",
          type: "select",
          options: RENTAL_LEASE_STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
          allowEmpty: false,
        },
        {
          id: "property_address",
          label: "Property address",
          type: "text",
          placeholder: "Enter address",
        },
        {
          id: "asset_id",
          label: "Asset",
          type: "select",
          allowEmpty: true,
          options: [
            { value: "", label: "— (clear)" },
            ...assetOptions.map((a) => ({ value: a.id, label: a.name || a.id })),
          ],
        },
        {
          id: "landlords",
          label: "Landlord(s)",
          type: "text",
          placeholder: "Enter landlords (blank to clear)",
        },
        {
          id: "start_date",
          label: "Start date",
          type: "date",
        },
        {
          id: "end_date",
          label: "End date",
          type: "date",
        },
        {
          id: "rent_amount",
          label: "Monthly rent",
          type: "text",
          placeholder: "Enter rent (blank to clear)",
        },
        {
          id: "security_deposit",
          label: "Security deposit",
          type: "text",
          placeholder: "Enter security deposit (blank to clear)",
        },
        {
          id: "pet_deposit",
          label: "Pet deposit",
          type: "text",
          placeholder: "Enter pet deposit (blank to clear)",
        },
      ]}
    />
  );
}
